import type { SelectOption } from "@/components/ui/Select";
import { formatDate, formatDateTime, formatMoney } from "@/lib/format";
import { formatIban } from "@/lib/validators";
import {
  EDUCATION_LEVELS,
  EMIRATES,
  GENDERS,
  MARITAL_STATUSES,
  NATIONALITIES,
  VISA_TYPES,
} from "@/features/profile/options";
import type { EmployeeProfile } from "@/features/profile/types";

/**
 * Every field the employee fills in at sign-up, described once.
 *
 * The review screen shows them in the order of the sign-up form, section by
 * section, and lets HR change any of them; the form steps show HR's crosses
 * under the same fields. Both read this list, so a field added to the form
 * is one entry here rather than a row in three screens.
 */

export type FieldKind =
  | "text"
  /** Arabic script, set right to left. */
  | "arabic"
  | "textarea"
  | "email"
  | "date"
  | "select"
  /** A country, stored as its two-letter code and shown by name. */
  | "country"
  /** A UAE mobile number: the server refuses any other. */
  | "mobile"
  /** Any country's phone number, with its code. */
  | "phone"
  | "emirates_id"
  | "iban"
  | "money"
  | "number"
  | "departments"
  /** Fixed weekdays or rotating days off: two columns, one answer. */
  | "days_off"
  /** A copy of a paper, opened and replaced rather than typed. */
  | "file"
  /** Shown, never changed: the declaration the employee accepted. */
  | "readonly";

export interface FieldMeta {
  key: string;
  label: string;
  kind: FieldKind;
  options?: readonly SelectOption[];
  /** The upload slot a copy fills, for a file field. */
  uploadKind?: string;
  /** Hint under the input when HR changes it. */
  hint?: string;
  /**
   * Changed somewhere else on the review screen: the employee number in the
   * decision panel, the job, the dates and the pay in the corrections, where
   * a pay change gets its reason. Shown here; not in this section's dialog.
   */
  editedIn?: "decision" | "corrections";
  /** Only listed when there is something to show: an older upload. */
  onlyWhenSet?: boolean;
  /**
   * Only for a UAE national ("national"), or only for anybody else
   * ("foreign"). Listed all the same when it holds something, so nothing
   * on the record is hidden.
   */
  nationality?: "national" | "foreign";
}

/** Whether a field asked only of some nationalities is asked of this person. */
function asked(meta: FieldMeta, profile: EmployeeProfile): boolean {
  if (!meta.nationality) return true;
  const national = (profile.nationality ?? "").toUpperCase() === "AE";
  return meta.nationality === "national" ? national : !national;
}

export interface SectionMeta {
  /** The sign-up step's own key, so a section links to its step. */
  key: string;
  title: string;
  fields: readonly FieldMeta[];
}

const field = (
  key: string,
  label: string,
  kind: FieldKind = "text",
  extra: Partial<FieldMeta> = {},
): FieldMeta => ({ key, label, kind, ...extra });

export const REVIEW_SECTIONS: readonly SectionMeta[] = [
  {
    key: "personal",
    title: "Personal details",
    fields: [
      field("full_name_en", "Name in English"),
      field("full_name_ar", "Name in Arabic", "arabic"),
      field("gender", "Gender", "select", { options: GENDERS }),
      field("date_of_birth", "Date of birth", "date"),
      field("nationality", "Nationality", "country"),
      field("marital_status", "Marital status", "select", { options: MARITAL_STATUSES }),
      field("profile_picture_file_id", "Photo", "file", { uploadKind: "profile_picture" }),
    ],
  },
  {
    key: "contact",
    title: "Contact details",
    fields: [
      field("personal_mobile", "Mobile number", "mobile", { hint: "A UAE mobile number." }),
      field("whatsapp_number", "WhatsApp number", "phone", {
        hint: "Any country's number, with its code.",
      }),
      field("personal_email", "Personal email", "email"),
      field("emirate_of_residence", "Emirate", "select", { options: EMIRATES }),
      field("full_address", "Home address", "textarea"),
    ],
  },
  {
    key: "emergency",
    title: "Emergency contact",
    fields: [
      field("emergency_contact_name", "Name"),
      field("emergency_contact_relationship", "Relationship"),
      field("emergency_contact_mobile", "Mobile", "phone"),
    ],
  },
  {
    key: "nomination",
    title: "Nomination",
    fields: [
      field("nominee_name", "Nominee"),
      field("nominee_relationship", "Relationship"),
      field("nominee_nationality", "Nationality", "country"),
      field("nominee_mobile", "Mobile", "phone"),
      field("nominee_address", "Address", "textarea"),
    ],
  },
  {
    key: "employment",
    title: "Employment",
    fields: [
      field("employee_id", "Employee number given", "text", { editedIn: "decision" }),
      field("position_title", "Position", "text", { editedIn: "corrections" }),
      field("departments", "Departments", "departments", { editedIn: "corrections" }),
      field("date_of_joining", "Date of joining", "date", { editedIn: "corrections" }),
    ],
  },
  {
    key: "emirates-id",
    title: "Emirates ID",
    fields: [
      field("emirates_id_number", "Emirates ID number", "emirates_id"),
      field("emirates_id_issue", "Issue date", "date"),
      field("emirates_id_expiry", "Expiry date", "date"),
      field("emirates_id_front_file_id", "Copy", "file", { uploadKind: "emirates_id_front" }),
      field("emirates_id_back_file_id", "Back (older upload)", "file", {
        uploadKind: "emirates_id_back",
        onlyWhenSet: true,
      }),
    ],
  },
  {
    key: "passport-visa",
    title: "Passport and visa",
    fields: [
      field("passport_number", "Passport number"),
      field("passport_issue", "Passport issue date", "date"),
      field("passport_expiry", "Passport expiry date", "date"),
      field("passport_file_id", "Passport copy", "file", { uploadKind: "passport_copy" }),
      // A UAE national holds no visa and brings the family book in its place.
      field("visa_type", "Visa type", "select", { options: VISA_TYPES, nationality: "foreign" }),
      field("visa_number", "Visa number", "text", { nationality: "foreign" }),
      field("visa_issue", "Visa issue date", "date", { nationality: "foreign" }),
      field("visa_expiry", "Visa expiry date", "date", { nationality: "foreign" }),
      field("visa_file_id", "Visa copy", "file", {
        uploadKind: "visa_copy",
        nationality: "foreign",
      }),
      field("family_book_file_id", "Family book", "file", {
        uploadKind: "family_book",
        nationality: "national",
      }),
    ],
  },
  {
    key: "labour-insurance",
    title: "Labour card and insurance",
    fields: [
      field("labour_card_number", "Labour card number"),
      field("labour_card_issue", "Labour card issue date", "date"),
      field("labour_card_expiry", "Labour card expiry date", "date"),
      field("labour_card_file_id", "Labour card copy", "file", { uploadKind: "labour_card" }),
      field("health_insurance_provider", "Insurance provider"),
      field("health_insurance_number", "Policy number"),
      field("health_insurance_issue", "Cover starts", "date"),
      field("health_insurance_expiry", "Cover ends", "date"),
      field("health_insurance_file_id", "Insurance card", "file", {
        uploadKind: "health_insurance",
      }),
    ],
  },
  {
    key: "contract-pay",
    title: "Contract and pay",
    fields: [
      field("contract_start", "Contract start", "date", { editedIn: "corrections" }),
      field("contract_expiry", "Contract expiry", "date", { editedIn: "corrections" }),
      field("contract_file_id", "Signed contract", "file", { uploadKind: "contract_copy" }),
      field("basic_salary", "Basic salary", "money", { editedIn: "corrections" }),
      field("allowances", "Allowances", "money", { editedIn: "corrections" }),
      field("bank_name", "Bank"),
      field("iban", "IBAN", "iban"),
      field("account_holder_name", "Account holder"),
      field("iban_letter_file_id", "IBAN letter", "file", { uploadKind: "iban_letter" }),
    ],
  },
  {
    key: "hours",
    title: "Working hours and days off",
    fields: [
      field("hours_per_day", "Hours per day", "number"),
      field("weekly_off_days", "Days off", "days_off"),
    ],
  },
  {
    key: "education",
    title: "Education and papers",
    fields: [
      field("highest_education", "Highest qualification", "select", {
        options: EDUCATION_LEVELS,
      }),
      field("cv_file_id", "CV", "file", { uploadKind: "cv" }),
      field("degree_file_id", "Degree certificate", "file", { uploadKind: "degree_certificate" }),
      field("experience_letter_file_id", "Experience letter", "file", {
        uploadKind: "experience_letter",
      }),
      field("other_file_id", "Other document", "file", { uploadKind: "other" }),
      field("police_clearance_file_id", "Police clearance certificate", "file", {
        uploadKind: "police_clearance",
      }),
    ],
  },
  {
    key: "signature",
    title: "Signature",
    fields: [
      field("signature_file_id", "Signature", "file", { uploadKind: "signature" }),
      field("declaration_accepted_at", "Declaration accepted", "readonly"),
    ],
  },
];

/** Every field above, by key. */
export const FIELD_META: Readonly<Record<string, FieldMeta>> = Object.fromEntries(
  REVIEW_SECTIONS.flatMap((section) => section.fields.map((meta) => [meta.key, meta])),
);

/** The section, and so the sign-up step, a field belongs to. */
export const SECTION_OF_FIELD: Readonly<Record<string, string>> = Object.fromEntries(
  REVIEW_SECTIONS.flatMap((section) => section.fields.map((meta) => [meta.key, section.key])),
);

/** The fields of a section that apply to this profile. */
export function sectionFields(section: SectionMeta, profile: EmployeeProfile): FieldMeta[] {
  return section.fields.filter((meta) => {
    const set = Boolean(valueOf(profile, meta.key));
    return set || (!meta.onlyWhenSet && asked(meta, profile));
  });
}

/** The keys of every field on the review screen that can be ticked or crossed. */
export function reviewFieldKeys(profile: EmployeeProfile): string[] {
  return REVIEW_SECTIONS.flatMap((section) =>
    sectionFields(section, profile)
      .filter((meta) => meta.kind !== "readonly")
      .map((meta) => meta.key),
  );
}

/** The fields HR changes in a section's own dialog. */
export function dialogFields(section: SectionMeta, profile: EmployeeProfile): FieldMeta[] {
  return sectionFields(section, profile).filter(
    (meta) => !meta.editedIn && meta.kind !== "readonly",
  );
}

export function valueOf(profile: EmployeeProfile, key: string): unknown {
  return (profile as unknown as Record<string, unknown>)[key];
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function optionLabel(options: readonly SelectOption[], value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  return options.find((option) => option.value === value)?.label ?? String(value);
}

/** A country code as the country's name, falling back to the code. */
export function countryName(code: string | null | undefined): string | null {
  return optionLabel(NATIONALITIES, code);
}

/** The days off in words: the weekdays, or that they rotate. */
export function daysOffText(rotates: boolean | undefined, days: readonly number[] | null): string | null {
  if (rotates) return "Rotational, set on the rota";
  const named = (days ?? []).map((day) => WEEKDAYS[day - 1]).filter(Boolean);
  return named.length ? named.join(", ") : null;
}

/** A field's value as a person reads it, or null when there is none. */
export function displayValue(profile: EmployeeProfile, meta: FieldMeta): string | null {
  const value = valueOf(profile, meta.key);
  switch (meta.kind) {
    case "date":
      return value ? formatDate(value as string) : null;
    case "readonly":
      return value ? formatDateTime(value as string) : null;
    case "select":
      return optionLabel(meta.options ?? [], value);
    case "country":
      return countryName(value as string | null);
    case "money":
      return value ? `${formatMoney(value as string)} AED` : null;
    case "number":
      return value ? `${Number(value)} hours` : null;
    case "departments":
      return Array.isArray(value) && value.length ? value.join(", ") : null;
    case "days_off":
      // A position that works to the rota decides it, whatever weekday the
      // record still holds from before the position was set.
      return daysOffText(
        profile.rotates_days_off || profile.days_off_rotate,
        profile.weekly_off_days,
      );
    case "iban":
      return value ? formatIban(value as string) : null;
    case "file":
      return value ? "Uploaded" : null;
    default:
      return value === null || value === undefined || value === "" ? null : String(value);
  }
}

// ── The dialog's form ────────────────────────────────────────────────────────

/** What the dialog's form starts from: the record's values, empty as "". */
export function formDefaults(
  fields: readonly FieldMeta[],
  profile: EmployeeProfile,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const meta of fields) {
    if (meta.kind === "file") continue;
    if (meta.kind === "days_off") {
      values.days_off_rotate = Boolean(profile.days_off_rotate);
      values.weekly_off_days = [...(profile.weekly_off_days ?? [])];
      continue;
    }
    const value = valueOf(profile, meta.key);
    values[meta.key] = value === null || value === undefined ? "" : value;
  }
  return values;
}

function same(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
  }
  const blank = (v: unknown) => v === null || v === undefined || v === "";
  if (blank(a) && blank(b)) return true;
  return String(a) === String(b);
}

/**
 * Only what HR changed, ready to send.
 *
 * An emptied field is sent as null, which clears it. Rotating days off go
 * with an empty weekday list, as on the sign-up form, and fixed days say so.
 */
export function sectionChanges(
  fields: readonly FieldMeta[],
  profile: EmployeeProfile,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};
  for (const meta of fields) {
    if (meta.kind === "file" || meta.kind === "readonly" || meta.editedIn) continue;
    if (meta.kind === "days_off") {
      const rotates = Boolean(values.days_off_rotate);
      const days = rotates ? [] : ((values.weekly_off_days as number[] | undefined) ?? []);
      if (rotates !== Boolean(profile.days_off_rotate)) changes.days_off_rotate = rotates;
      if (!same(days, profile.weekly_off_days ?? [])) {
        changes.weekly_off_days = days;
        if (!rotates) changes.days_off_rotate = false;
      }
      continue;
    }
    const next = values[meta.key];
    if (same(next, valueOf(profile, meta.key))) continue;
    changes[meta.key] = typeof next === "string" ? next.trim() || null : next;
  }
  return changes;
}

/** A light check before sending; the server has the last word. */
export function checkValues(
  fields: readonly FieldMeta[],
  values: Record<string, unknown>,
): Record<string, string> {
  const problems: Record<string, string> = {};
  for (const meta of fields) {
    const value = values[meta.key];
    if (meta.kind === "date" && typeof value === "string" && value !== "") {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(Date.parse(value))) {
        problems[meta.key] = "Enter a date.";
      }
    }
    if (meta.kind === "email" && typeof value === "string" && value.trim() !== "") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        problems[meta.key] = "Enter an email address.";
      }
    }
    if (meta.kind === "number" && value !== "" && value !== null && value !== undefined) {
      const hours = Number(value);
      if (!(hours > 0 && hours <= 24)) problems[meta.key] = "Enter between 1 and 24 hours.";
    }
    if (meta.kind === "days_off") {
      const rotates = Boolean(values.days_off_rotate);
      const days = (values.weekly_off_days as number[] | undefined) ?? [];
      if (!rotates && days.length === 0) problems[meta.key] = "Choose at least one day off.";
      if (days.length >= 7) problems[meta.key] = "At least one day a week must be a working day.";
    }
  }
  return problems;
}
